import { CordataSearchWorksResponse } from "@biblioteksentralen/bmdb-search-client";
import { bmdbFetcher } from "@libry-content/integrations";
import { sift } from "radash";
import { useEffect } from "react";
import useSWR from "swr";
import useSWRInfinite from "swr/infinite";
import { useCommonData } from "../../../components/layout/CommonDataProvider";
import { Translate, useTranslation } from "../../../utils/hooks/useTranslation";
import { getSearchConfig, SiteSearchConfig } from "../../searchApi/searchConfig";
import { useFacetAndFilterContext } from "./FacetAndFilterContext";
import { SearchCategory, SearchResult } from "./SearchContext";
import SearchResultsWork from "./SearchResultsWork";
import { workSearchCategories, WorkSearchCategory } from "./types";
import { SearchState } from "./useSearchState";

const getWorkSearchCategoryNameMap = (t: Translate["t"]): Record<WorkSearchCategory, string> => ({
  book: t("Bøker"),
  videogame: t("Spill"),
  movie: t("Filmer"),
  music: t("Musikk"),
});

type WorkSearchKey = {
  search: "work";
  query: string | undefined;
  page: number;
  mediaTypeAndFilters: { "plas.mediaTypes": string[] } | undefined;
} | null;

export const getMediaTypeFilters = (category: SearchCategory) => {
  //include sheet music as filter when searching for music
  if (category === "music") return ["music", "sheetMusic"];
  //include sheet audiobook as filter when searching for book
  if (category === "book") return ["book", "audiobook"];
  return [category];
};

export const useWorkSearch = (
  searchState: SearchState,
  setSearchState: (searchState: NonNullable<SearchState>) => void
): SearchResult => {
  const { t } = useTranslation();
  const { site } = useCommonData();
  const siteSearchConfig = getSearchConfig(site);

  const { filtersInUse } = useFacetAndFilterContext();
  const { relevantPages: workSearchCategories, isLoading: areCategoriesLoading } = useRelevantCategoriesForWorkSearch(
    searchState?.s,
    siteSearchConfig
  );

  const mediaTypeAndFilters = {
    "plas.mediaTypes":
      searchState?.selectedCategory &&
      workSearchCategories.find((workSearchCategory) => workSearchCategory === searchState.selectedCategory)
        ? getMediaTypeFilters(searchState.selectedCategory)
        : [],
    ...filtersInUse,
  };

  const getKey = (pageIndex: number, previousPageData: CordataSearchWorksResponse): WorkSearchKey => {
    if (previousPageData && previousPageData.endOfResults) return null;
    return { search: "work", query: searchState?.s, page: pageIndex + 1, mediaTypeAndFilters };
  };

  const workFetcher = (key: WorkSearchKey) => {
    return bmdbFetcher({
      operation: "searchWorks",
      params: {
        size: 9,
        query: key?.query,
        page: key?.page,
        filter: key?.mediaTypeAndFilters,
      },
      siteSearchConfig,
    });
  };

  const {
    data,
    isLoading: areSearchResultsLoading,
    isValidating,
    setSize,
  } = useSWRInfinite(getKey, workFetcher, {
    initialSize: searchState?.work?.pagination ?? 1,
    revalidateOnFocus: false,
    revalidateIfStale: false,
  });

  useEffect(() => {
    setSize(searchState?.work?.pagination ?? 1);
  }, [setSize, searchState?.work?.pagination]);

  const works = data ? sift(data.map((result) => result?.results)).flatMap((works) => works) : [];
  const lastResult = data?.[data.length - 1];
  const endOfResults = lastResult ? lastResult.endOfResults : true;

  const handlePagination = () => {
    const pagination = searchState?.work?.pagination ? searchState.work.pagination + 1 : 2;
    setSearchState({ work: { ...searchState?.work, pagination } });
  };

  const workSearchComponent = (
    <SearchResultsWork
      works={works}
      endOfResults={endOfResults}
      isValidating={isValidating}
      paginate={handlePagination}
    />
  );

  return {
    summary: workSearchComponent,
    categoryResults: Object.values(workSearchCategories).map((relevantCategory) => ({
      id: relevantCategory,
      label: getWorkSearchCategoryNameMap(t)[relevantCategory],
      children: workSearchComponent,
    })),
    areSearchResultsLoading,
    areCategoriesLoading,
  };
};

const useRelevantCategoriesForWorkSearch = (
  query: string | undefined,
  siteSearchConfig: SiteSearchConfig
): { relevantPages: WorkSearchCategory[]; isLoading: boolean } => {
  const key = { operation: "checkPagesQuery", query };
  const { data, isLoading } = useSWR(key, (key) => {
    return bmdbFetcher({
      operation: "searchWorks",
      params: {
        size: 0,
        query: key?.query,
        facet: ["plas.mediaTypes"],
        facet_terms_size: 10,
      },
      siteSearchConfig,
    });
  });
  const mediaTypes = data?.facets?.find((facet) => facet.type === "plas.mediaTypes")?.terms.map(({ value }) => value);

  const relevantPages: WorkSearchCategory[] = workSearchCategories.filter((workSearchCategory) =>
    mediaTypes?.includes(workSearchCategory)
  );

  return { relevantPages, isLoading };
};
